import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Share2, X, ChevronLeft, ChevronRight, Heart, Sparkles, Clock, Copy, Palette, Target, Users, Monitor, Brain, LayoutGrid, Type, Link as LinkIcon, MousePointer2, ExternalLink, Globe, Check, Loader2, Lock, MoreVertical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
// Removed motion library for memory optimization - using CSS transitions instead
import type { Ad } from "@/components/AdCard";
import { getStorageUrl } from "@/lib/storage";
import { toast } from "@/hooks/use-toast";
import { useFavorites } from "@/context/FavoritesContext";
import GradientHeart from "@/components/GradientHeart";
import { useEntitlements } from "@/entitlements/useEntitlements";
import ObyectLoader from "@/components/ObyectLoader";
import { logger } from "@/lib/logger";


interface AdDetailProps {
  modal?: boolean;
}

/** Gradient banner CTA that accepts an exact pixel width */
const BannerCTA = ({ width }: { width?: number }) => {
  const bannerStyle: React.CSSProperties = {
    ...(width ? { width } : {}),
    boxSizing: "border-box",
    backdropFilter: "blur(4px)",
    background: 'linear-gradient(to bottom, #70BAFF, #258BFF)'
    // `
    //   radial-gradient(at bottom left, #e2466c, transparent 85%),
    //   radial-gradient(at bottom right, #d088ff, transparent 70%),
    //   radial-gradient(at top left, #765be6, transparent 70%),
    //   radial-gradient(at top right, #bcbdda, transparent 70%)
    // `
    ,
  };

  return (
    <a
      href="/library"
      style={bannerStyle}
      className="
        mt-6
        rounded-2xl
        text-white
        shadow-lg
        px-5 md:px-6
        py-5
        mx-auto
        flex flex-col items-center justify-center text-center
        hover:opacity-90 transition
        cursor-pointer
      "
    >
      <span className="text-2xl md:text-xl font-medium mb-3">
        More <i>winning</i> creatives?
      </span>
      <div className="
          rounded-full bg-black text-white
          text-xs md:text-sm
          px-4 md:px-5 py-2
          inline-flex items-center gap-2
        "
      >
        <span>🚀</span>
        <span>Get started for free</span>
      </div>
    </a>
  );
};


const AdDetail = ({ modal }: AdDetailProps) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backgroundLocation = (location.state as any)?.backgroundLocation;
  const isModal = modal ?? Boolean(backgroundLocation);

  const [ad, setAd] = useState<any>(null);
  const [analysis, setAnalysis] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [ids, setIds] = useState<string[]>([]);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const { isSubscribed, entitlementLoaded, isAuthenticated } = useEntitlements();

  // Only “ready” once hook has resolved to true/false, not undefined/null
  const entitlementsReady = entitlementLoaded && typeof isSubscribed === "boolean";


  // track image width to mirror on the banner
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imgWidth, setImgWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    const el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const href = `${window.location.origin}/ads/${id}`;
    if (el) el.href = href;
    else {
      const link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      link.setAttribute("href", href);
      document.head.appendChild(link);
    }
  }, [id]);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        // Query with correct column names: ad_copy, launch_date, end_date
        const { data: row, error: queryError } = await (supabase as any)
          .from("ads")
          .select(`
            id, brand_id, category_id, image_path, created_at, ad_copy, launch_date, end_date, title, cta_text, link_url, ad_library_url, display_format,
            ad_analysis (
              ad_type,
              awareness_phase,
              scroll_trigger_score,
              scroll_trigger_analysis,
              tension_trigger_score,
              tension_trigger_analysis,
              tension_type,
              tension_internal_thought,
              relief_trigger_score,
              relief_trigger_analysis,
              relief_type,
              decision_trigger_score,
              decision_trigger_analysis,
              replication_copy_rules,
              replication_design_rules
            )
          `)
          .eq("id", id)
          .maybeSingle();

        if (queryError) {
          logger.error("Failed to load ad", queryError);
          toast({
            title: "Error loading ad",
            description: queryError.message || "Could not load the ad.",
            variant: "destructive" as any,
          });
          setLoading(false);
          return;
        }

        if (!row) {
          toast({
            title: "Not found",
            description: "This ad could not be found.",
            variant: "destructive" as any,
          });
          setLoading(false);
          return;
        }

        const [bResp, cResp] = await Promise.all([
          (supabase as any)
            .from("brands")
            .select("name,logo_path")
            .eq("id", row.brand_id)
            .maybeSingle(),
          (supabase as any)
            .from("categories")
            .select("name")
            .eq("id", row.category_id)
            .maybeSingle(),
        ]);

        const brandName = bResp.data?.name ?? "Unknown brand";
        const categoryName = cResp.data?.name ?? "Unknown category";

        const { data: imgPub } = { data: { publicUrl: getStorageUrl("ad-images", row.image_path) ?? "" } };

        let brandLogoUrl: string | undefined;
        if (bResp.data?.logo_path) {
          const { data: pub } = { data: { publicUrl: getStorageUrl("brand-logos", bResp.data.logo_path) ?? "" } };
          brandLogoUrl = pub.publicUrl;
        }

        // Determine if still active: end_date equals launch_date means still running
        const launchDate = row.launch_date || row.created_at;
        const endDate = row.end_date;
        const isStillActive = endDate && launchDate && endDate === launchDate;

        const mapped: any = {
          id: row.id,
          brand: brandName,
          category: categoryName,
          image: imgPub.publicUrl,
          imagePath: row.image_path,
          brandLogoUrl,
          copy: row.ad_copy || "",
          start: launchDate,
          end: isStillActive ? "" : (endDate || ""),
          title: row.title || "",
          ctaText: row.cta_text || "",
          linkUrl: row.link_url || "",
          adLibraryUrl: row.ad_library_url || "",
          displayFormat: row.display_format || "",
          ctr: 0,
          conversions: 0,
          engagement: 0,
        };
        setAd(mapped);
        if (row.ad_analysis) {
          setAnalysis(row.ad_analysis);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    const loadIds = async () => {
      const { data } = await (supabase as any)
        .from("ads")
        .select("id")
        .order("created_at", { ascending: false });
      setIds((data ?? []).map((r: any) => r.id));
    };
    loadIds();
  }, []);

  // Observe image width so banner can match
  useEffect(() => {
    if (!imgRef.current) return;

    const setWidth = () => {
      if (imgRef.current) setImgWidth(imgRef.current.getBoundingClientRect().width);
    };

    setWidth();

    const ro = new ResizeObserver(() => setWidth());
    ro.observe(imgRef.current);
    window.addEventListener("resize", setWidth);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setWidth);
    };
  }, [ad?.image]);

  useEffect(() => {
    const title = ad ? `${ad.brand} – Ad Detail` : "Ad Detail";
    document.title = title.slice(0, 60);
    const meta = document.querySelector('meta[name="description"]');
    const desc = ad
      ? `View ${ad.brand} ${ad.category} ad in full screen and share.`
      : "View this ad in full screen.";
    if (meta) meta.setAttribute("content", desc.slice(0, 160));
  }, [ad]);

  useEffect(() => {
    if (!isModal) return;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, [isModal]);

  const close = () => {
    if (isModal && backgroundLocation) {
      navigate(
        String(backgroundLocation.pathname) +
          (backgroundLocation.search ?? "") +
          (backgroundLocation.hash ?? ""),
        { replace: true }
      );
    } else {
      navigate("/library");
    }
  };

  const buildShareUrl = () => `${window.location.origin}/ads/${id}`;

  const handleCopyAd = async () => {
    try {
      await navigator.clipboard.writeText(buildShareUrl());
      toast({
        title: "Link copied",
        description: "Ad link copied to clipboard.",
      });
    } catch (e: any) {
      toast({
        title: "Copy failed",
        description: e?.message ?? "Could not copy the link",
        variant: "destructive" as any,
      });
    }
  };


  const handleNativeShareAd = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: ad?.brand ? `${ad.brand} Ad` : "Ad",
          text: ad ? `${ad.brand} • ${ad.category}` : "Ad from library",
          url: buildShareUrl(),
        });
        setShareOpen(false);
        return;
      }
    } catch {
      // fall back below
    }
    try {
      await navigator.clipboard.writeText(buildShareUrl());
      toast({
        title: "Link copied",
        description: "Native share not supported. Link copied.",
      });
      setShareOpen(false);
    } catch (e: any) {
      toast({
        title: "Share failed",
        description: e?.message ?? "Could not share the link",
        variant: "destructive" as any,
      });
    }
  };

  const formatAnalysisAsMarkdown = () => {
    if (!analysis || !ad) return "";

    let markdown = `# ${ad.brand} - Ad Breakdown\n\n`;
    markdown += `**Category:** ${ad.category}\n`;
    markdown += `**Ad Type:** ${analysis.ad_type || "N/A"}\n`;
    markdown += `**Awareness Phase:** ${analysis.awareness_phase || "N/A"}\n\n`;
    markdown += `---\n\n`;

    // Scroll Trigger
    markdown += `## 1. Scroll Trigger\n\n`;
    if (analysis.scroll_trigger_score) {
      markdown += `**Score:** ${analysis.scroll_trigger_score}/10\n\n`;
    }
    markdown += `${analysis.scroll_trigger_analysis || "Analysis not available."}\n\n`;

    // Tension Trigger
    markdown += `## 2. Tension Trigger\n\n`;
    if (analysis.tension_trigger_score) {
      markdown += `**Score:** ${analysis.tension_trigger_score}/10\n\n`;
    }
    markdown += `${analysis.tension_trigger_analysis || "Analysis not available."}\n\n`;
    if (analysis.tension_type) {
      markdown += `**Type:** ${analysis.tension_type.replace(/_/g, " ")}\n\n`;
    }
    if (analysis.tension_internal_thought) {
      markdown += `> "${analysis.tension_internal_thought}"\n\n`;
    }

    // Relief Trigger
    markdown += `## 3. Relief Trigger\n\n`;
    if (analysis.relief_trigger_score) {
      markdown += `**Score:** ${analysis.relief_trigger_score}/10\n\n`;
    }
    markdown += `${analysis.relief_trigger_analysis || "Analysis not available."}\n\n`;
    if (analysis.relief_type) {
      markdown += `**Type:** ${analysis.relief_type}\n\n`;
    }

    // Decision Trigger
    markdown += `## 4. Decision Trigger\n\n`;
    if (analysis.decision_trigger_score) {
      markdown += `**Score:** ${analysis.decision_trigger_score}/10\n\n`;
    }
    markdown += `${analysis.decision_trigger_analysis || "Analysis not available."}\n\n`;

    // Replication Rules
    markdown += `## 5. Re-create Rules\n\n`;
    
    markdown += `### Copy Rules\n\n`;
    if (analysis.replication_copy_rules?.length > 0) {
      analysis.replication_copy_rules.forEach((rule: string) => {
        markdown += `- ${rule}\n`;
      });
    } else {
      markdown += `- No copy rules available\n`;
    }
    markdown += `\n`;

    markdown += `### Design Rules\n\n`;
    if (analysis.replication_design_rules?.length > 0) {
      analysis.replication_design_rules.forEach((rule: string) => {
        markdown += `- ${rule}\n`;
      });
    } else {
      markdown += `- No design rules available\n`;
    }

    return markdown;
  };

  const handleCopyBreakdown = async () => {
    try {
      const markdown = formatAnalysisAsMarkdown();
      await navigator.clipboard.writeText(markdown);
      toast({
        title: "Breakdown copied",
        description: "Ad breakdown copied to clipboard in Markdown format.",
      });
    } catch (e: any) {
      toast({
        title: "Copy failed",
        description: e?.message ?? "Could not copy the breakdown",
        variant: "destructive" as any,
      });
    }
  };

  

  const brandInitial = useMemo(
    () => ad?.brand?.charAt(0).toUpperCase() ?? "?",
    [ad?.brand]
  );
  const currentIndex = useMemo(
    () => (id ? ids.indexOf(id) : -1),
    [ids, id]
  );

  const goPrev = () => {
    if (!isSubscribed || ids.length === 0 || currentIndex === -1) return;
    const prevIndex = (currentIndex - 1 + ids.length) % ids.length;
    navigate(`/ads/${ids[prevIndex]}`, { state: location.state });
  };
  const goNext = () => {
    if (!isSubscribed || ids.length === 0 || currentIndex === -1) return;
    const nextIndex = (currentIndex + 1) % ids.length;
    navigate(`/ads/${ids[nextIndex]}`, { state: location.state });
  };

  const { isFavorite, toggleFavorite } = useFavorites();
  const favActive = ad ? isFavorite(ad.id) : false;

  // Calculate running days
  const runningDays = useMemo(() => {
    if (!ad?.start) return null;
    try {
      const startDate = new Date(ad.start);
      const endDate = ad.end ? new Date(ad.end) : new Date();
      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  }, [ad?.start, ad?.end]);

  const isStillRunning = !ad?.end;

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't trigger shortcuts if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      
      const key = e.key.toLowerCase();
      
      // Escape - close
      if (e.key === "Escape") close();
      
      // Arrow keys - navigate (subscribers only)
      if (isSubscribed && e.key === "ArrowLeft") goPrev();
      if (isSubscribed && e.key === "ArrowRight") goNext();
      
      // F - toggle favorite
      if (key === "f" && ad) {
        e.preventDefault();
        toggleFavorite(ad.id);
      }
      
      // C - copy link
      if (key === "c" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        const url = `${window.location.origin}/ads/${id}`;
        navigator.clipboard.writeText(url).then(() => {
          toast({ title: "Link copied", description: "Ad link copied to clipboard." });
        }).catch(() => {
          toast({ title: "Failed", description: "Could not copy link.", variant: "destructive" });
        });
      }
      
      // R - use as request inspiration
      if (key === "r") {
        e.preventDefault();
        navigate(`/requests/new?inspiration=${id}`);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ids, id, isSubscribed, ad, toggleFavorite, navigate, close, goPrev, goNext]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <ObyectLoader src="/lovable-uploads/obyect_new.svg" />
        </div>
      </div>
    );
  }

  if (!ad) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-black uppercase tracking-tight text-muted-foreground/40">CREATIVE DELETED OR PRIVATE</h2>
          <Button onClick={() => navigate("/library")} variant="outline" className="rounded-xl uppercase font-black text-[10px] tracking-widest">
            Return to Library
          </Button>
        </div>
      </div>
    );
  }

  const content = (
    <div className="mx-auto w-full max-w-[1800px] px-4 md:px-8 py-8 animate-fade-in relative">
      {/* Top Header Section */}
      <div className="flex items-center justify-between mb-10 pb-6 border-b border-white/5">
        <div className="flex items-center gap-2 md:gap-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-10 w-10 md:h-12 md:w-12 rounded-2xl bg-white/5 border border-white/5 hover:bg-white/10"
            title="Go back"
          >
            <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" />
          </Button>
          <Avatar className="h-10 w-10 md:h-14 md:w-14 border-2 border-white/5 shadow-2xl">
            {ad.brandLogoUrl ? (
              <AvatarImage src={ad.brandLogoUrl} alt={`${ad.brand} logo`} />
            ) : null}
            <AvatarFallback className="bg-secondary/40 text-foreground/80 font-black">
              {brandInitial}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-2 md:gap-3">
              <h1 
                onClick={() => navigate(`/library?brand=${encodeURIComponent(ad.brand)}`)}
                className="text-sm md:text-2xl font-black uppercase tracking-tighter cursor-pointer hover:text-[#70BAFF] transition-colors"
              >
                {ad.brand}
              </h1>
              {runningDays !== null && (
                <div className="hidden md:flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#70BAFF]/10 border border-[#70BAFF]/20 text-xs md:text-[10px] font-black uppercase text-[#70BAFF] tracking-widest">
                  <Clock className="h-3 w-3" />
                  <span>{runningDays} DAYS {isStillRunning ? "ACTIVE" : ""}</span>
                </div>
              )}
            </div>
            <p 
              onClick={() => navigate(`/library?category=${encodeURIComponent(ad.category)}`)}
              className="text-[10px] md:text-xs font-black text-muted-foreground uppercase tracking-[0.2em] mt-0.5 md:mt-1 cursor-pointer hover:text-white transition-colors"
            >
              {ad.category}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => toggleFavorite(ad.id)}
            className="h-10 w-10 md:h-12 md:w-12 rounded-2xl bg-white/5 border border-white/5 hover:bg-white/10"
          >
            {favActive ? (
              <GradientHeart className="h-5 w-5 md:h-6 md:w-6" />
            ) : (
              <Heart className="h-5 w-5 md:h-6 md:w-6" />
            )}
          </Button>

          {/* Mobile: Dropdown Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden h-10 w-10 rounded-2xl bg-white/5 border border-white/5 hover:bg-white/10"
              >
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate(`/requests/new?inspiration=${id}`)}>
                <Sparkles className="h-4 w-4 mr-2" />
                Request Design
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCopyAd}>
                <Share2 className="h-4 w-4 mr-2" />
                Share Intelligence
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Desktop: Full Buttons */}
          <Button
            variant="ghost"
            onClick={() => navigate(`/requests/new?inspiration=${id}`)}
            className="hidden md:flex btn-glow h-12 px-6 rounded-xl text-white hover:opacity-90 font-bold capitalize text-base md:text-[10px] tracking-widest gap-2 shadow-lg"
            style={{
              backdropFilter: 'blur(4px)',
              background: 'linear-gradient(to bottom, #70BAFF, #258BFF)',
            }}
          >
            <Sparkles className="h-4 w-4" />
            Request design
          </Button>

          <Button
            variant="outline"
            onClick={handleCopyAd}
            className="hidden md:flex h-12 px-6 rounded-2xl bg-white/5 border-white/5 hover:bg-white/10 font-black uppercase text-base md:text-[10px] tracking-widest gap-2"
          >
            <Share2 className="h-4 w-4" />
            Share Intelligence
          </Button>

          <Button variant="ghost" size="icon" onClick={close} className="h-10 w-10 md:h-12 md:w-12 rounded-2xl">
            <X className="h-5 w-5 md:h-6 md:w-6" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-9 gap-10 items-start">
        {/* Left Canvas: Asset Preview (Span 4) */}
        <div className="lg:col-span-4 space-y-8 lg:sticky lg:top-8">
          <div className="rounded-[15px] border bg-card/30 backdrop-blur-3xl overflow-hidden shadow-[0_32px_64px_-12px_rgba(0,0,0,0.5)] border-white/5 group relative">
            <img
              ref={imgRef}
              src={ad.image}
              alt="Creative Intelligence"
              className="w-full h-auto object-contain bg-muted/20 transition-transform duration-700 group-hover:scale-[1.02]"
              loading="eager"
            />
            {isStillRunning && (
              <div className="absolute top-6 right-6 px-3 py-1.5 rounded-xl bg-[#70BAFF]/90 backdrop-blur-md shadow-lg text-white text-[10px] font-black uppercase tracking-widest">
                ACTIVE CREATIVE
              </div>
            )}
          </div>

          {/* Conditional CTA Section: Show Request Design if subscribed, otherwise show Banner */}
          {isAuthenticated && entitlementsReady && (
            isSubscribed ? (
              <Button
                onClick={() => navigate(`/requests/new?inspiration=${id}`)}
                className="btn-glow w-full h-14 rounded-xl text-white hover:opacity-90 transition-opacity font-bold capitalize text-sm tracking-widest shadow-lg"
                style={{
                  backdropFilter: 'blur(4px)',
                  background: 'linear-gradient(to bottom, #70BAFF, #258BFF)',
                }}
              >
                <Sparkles className="h-5 w-5 mr-2" />
                Request design
              </Button>
            ) : (
              <BannerCTA width={imgWidth} />
            )
          )}
        </div>

        {/* Center Canvas: Trigger-Based Intelligence Dashboard */}
        <div className="lg:col-span-5 space-y-6">
          <section className="space-y-6">
            {analysis ? (
              <div className="rounded-[15px] border border-white/5 bg-card/10 p-1 shadow-2xl overflow-hidden backdrop-blur-3xl">
                <div className="bg-card/20 rounded-[15px] p-6 md:p-8 space-y-8 border border-white/5 backdrop-blur-xl">
                  {/* Header with Framework + Awareness */}
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-white/5 border border-white/5 flex items-center justify-center">
                        <Brain className="h-5 w-5 text-[#70BAFF]" />
                      </div>
                      <div>
                        <h2 className="font-bold text-xl tracking-tight text-white/90">Ad Breakdown</h2>
                        <p className="text-xs md:text-[10px] text-muted-foreground/60 uppercase tracking-widest font-medium">Subyect's Analysis</p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                      <div className="flex flex-wrap gap-2">
                        <Badge className="bg-[#70BAFF]/10 text-[#70BAFF] border-[#70BAFF]/20 text-xs md:text-[10px] font-bold uppercase py-1.5 px-3">
                          {analysis.ad_type || "N/A"}
                        </Badge>
                        <Badge className="bg-[#70BAFF]/10 text-[#70BAFF] border-[#70BAFF]/20 text-xs md:text-[10px] font-bold uppercase py-1.5 px-3">
                          {analysis.awareness_phase || "N/A"}
                        </Badge>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyBreakdown}
                        className="h-8 px-3 rounded-xl bg-white/5 border-white/5 hover:bg-white/10 font-bold uppercase text-[10px] tracking-widest gap-2"
                      >
                        <Copy className="h-3.5 w-3.5" />
                        Copy
                      </Button>
                    </div>
                  </div>

                  {/* 1. Scroll Trigger */}
                  <div className="space-y-3 pt-6 border-t border-white/5">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base md:text-sm font-bold text-white/90">1. Scroll Trigger</h3>
                      {analysis.scroll_trigger_score && (
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-black text-[#70BAFF]">{analysis.scroll_trigger_score}</span>
                          <span className="text-xs text-muted-foreground/40">/10</span>
                        </div>
                      )}
                    </div>
                    <p className="text-base md:text-sm text-muted-foreground/80 leading-relaxed">
                      {analysis.scroll_trigger_analysis || "Analysis not available."}
                    </p>
                  </div>

                   {/* 2. Tension Trigger */}
                  <div className="space-y-3 pt-6 border-t border-white/5">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base md:text-sm font-bold text-white/90">2. Tension Trigger</h3>
                      {analysis.tension_trigger_score && (
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-black text-[#70BAFF]">{analysis.tension_trigger_score}</span>
                          <span className="text-xs text-muted-foreground/40">/10</span>
                        </div>
                      )}
                    </div>
                    <p className="text-base md:text-sm text-muted-foreground/80 leading-relaxed">
                      {analysis.tension_trigger_analysis || "Analysis not available."}
                    </p>
                    {analysis.tension_type && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-muted-foreground/40 uppercase">Type:</span>
                        <Badge className="bg-[#70BAFF]/10 text-[#70BAFF] border-[#70BAFF]/20 text-[9px] font-bold uppercase">
                          {analysis.tension_type.replace(/_/g, " ")}
                        </Badge>
                      </div>
                    )}
                    {analysis.tension_internal_thought && (
                      <p className="text-sm italic text-muted-foreground/60 pl-4 border-l-2 border-[#70BAFF]/30">
                        "{analysis.tension_internal_thought}"
                      </p>
                    )}
                  </div>

                  {/* 3. Relief Trigger */}
                  <div className="space-y-3 pt-6 border-t border-white/5">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base md:text-sm font-bold text-white/90">3. Relief Trigger</h3>
                      {analysis.relief_trigger_score && (
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-black text-[#70BAFF]">{analysis.relief_trigger_score}</span>
                          <span className="text-xs text-muted-foreground/40">/10</span>
                        </div>
                      )}
                    </div>
                    <p className="text-base md:text-sm text-muted-foreground/80 leading-relaxed">
                      {analysis.relief_trigger_analysis || "Analysis not available."}
                    </p>
                    {analysis.relief_type && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-muted-foreground/40 uppercase">Type:</span>
                        <Badge className="bg-[#70BAFF]/10 text-[#70BAFF] border-[#70BAFF]/20 text-[9px] font-bold uppercase">
                          {analysis.relief_type}
                        </Badge>
                      </div>
                    )}
                  </div>

                  {/* 4. Decision Trigger */}
                  <div className="space-y-3 pt-6 border-t border-white/5">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base md:text-sm font-bold text-white/90">4. Decision Trigger</h3>
                      {analysis.decision_trigger_score && (
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-black text-[#70BAFF]">{analysis.decision_trigger_score}</span>
                          <span className="text-xs text-muted-foreground/40">/10</span>
                        </div>
                      )}
                    </div>
                    <p className="text-base md:text-sm text-muted-foreground/80 leading-relaxed">
                      {analysis.decision_trigger_analysis || "Analysis not available."}
                    </p>
                  </div>

                  {/* 5. Replication Rules */}
                  <div className="space-y-4 pt-6 border-t border-white/5">
                    <h3 className="text-sm font-bold text-white/90">5. Re-create Rules</h3>
                    
                    <div className="grid sm:grid-cols-2 gap-6">
                      {/* Copy Rules */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Type className="h-4 w-4 text-[#70BAFF]" />
                          <span className="text-[10px] font-bold text-[#70BAFF] uppercase tracking-widest">Copy</span>
                        </div>
                        <ul className="space-y-2">
                          {analysis.replication_copy_rules?.length > 0 ? (
                            analysis.replication_copy_rules.map((rule: string, i: number) => (
                              <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground/80">
                                <span className="text-[#70BAFF] mt-0.5">•</span>
                                <span>{rule}</span>
                              </li>
                            ))
                          ) : (
                            <li className="text-xs text-muted-foreground/40">No copy rules available</li>
                          )}
                        </ul>
                      </div>

                       {/* Design Rules */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <LayoutGrid className="h-4 w-4 text-[#70BAFF]" />
                          <span className="text-[10px] font-bold text-[#70BAFF] uppercase tracking-widest">Design</span>
                        </div>
                        <ul className="space-y-2">
                          {analysis.replication_design_rules?.length > 0 ? (
                            analysis.replication_design_rules.map((rule: string, i: number) => (
                              <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground/80">
                                <span className="text-[#70BAFF] mt-0.5">•</span>
                                <span>{rule}</span>
                              </li>
                            ))
                          ) : (
                            <li className="text-xs text-muted-foreground/40">No design rules available</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-[15px] border border-white/5 bg-card/10 p-1 shadow-2xl overflow-hidden backdrop-blur-3xl">
                <div className="bg-card/20 rounded-[15px] p-8 space-y-6 border border-white/5 backdrop-blur-xl">
                  <div className="flex flex-col items-center justify-center text-center py-12">
                    <div className="h-16 w-16 rounded-xl bg-white/5 border border-white/5 flex items-center justify-center mb-4">
                      <Lock className="h-7 w-7 text-muted-foreground/40" />
                    </div>
                    <h3 className="text-lg font-bold text-muted-foreground/60 uppercase tracking-wide mb-2">No Analysis Available</h3>
                    <p className="text-xs text-muted-foreground/40 uppercase tracking-widest">AI breakdown not yet generated</p>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );

  if (!isModal) {
    return (
      <div className="min-h-screen">
        <main className="h-[calc(100vh-64px)]">{content}</main>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 overscroll-none overflow-hidden mt-4">
      {content}
    </div>
  );
};

export default AdDetail;
